import React, { FunctionComponent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { LinearGradient } from 'expo-linear-gradient'
import styled, { useTheme } from 'styled-components/native'

import { PATHWAYS_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'

import { BodyText } from '../../../atoms/bodyText/BodyText'
import { Display } from '../../../atoms/display/Display'
import { ArrowIcon, ArrowIconDirection } from '../../../atoms/icons/ArrowIcon'
import { OrbLottie } from '../../../atoms/orbLottie/OrbLottie'
import { PressableOpacity } from '../../../atoms/pressableOpacity/PressableOpacity'
import { PrimaryButton } from '../../../atoms/primaryButton/PrimaryButton'
import { TextButton } from '../../../atoms/textButton/TextButton'
import { Title } from '../../../atoms/title/Title'
import { WordCloudButton } from '../../../atoms/wordCloudButton/WordCloudButton'
import { BodyTextSize, DisplaySize, TitleSize } from '../../../styles/typeStyles'
import { AuroraThemeType } from '../../../utils/themes/ThemeProvider'
import { tID } from '../../../utils/utils'

export interface AuroraHeroSectionProps {
  pathwaysType?: PATHWAYS_TYPE
  onCreateAccountButtonPress?: () => void
  onProviderLedCardPress?: () => void
  onWellnessCheckinCardPress?: () => void
  onSelfCareCardPress?: () => void
  onNotifyMeOnLaunchPress?: () => void
  onExploreOnYourOwnCTAPress?: () => void
  selectedPillId?: string
  startDate?: string
  isRegistrationAndLoginDisabled?: boolean
  isAutoEnrollmentPathways?: boolean
  onSkipPress?: () => void
}

const HeroContainer = styled(LinearGradient)<{ theme: AuroraThemeType }>(({ theme }) => ({
  width: '100%',
  minHeight: theme.breakpoints.isMobileSized ? '600px' : '500px',
  paddingTop: theme.breakpoints.isMobileSized ? theme.spacing['48px'] : theme.spacing['64px'],
  paddingBottom: theme.breakpoints.isMobileSized ? theme.spacing['48px'] : theme.spacing['64px'],
  paddingHorizontal: theme.spacing['16px'],
  alignItems: 'center',
}))

const NavHeaderContainer = styled.View<{ theme: AuroraThemeType }>(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing['32px'],
  right: theme.spacing['32px'],
  zIndex: 1,
}))

const ContentContainer = styled.View<{ theme: AuroraThemeType }>(({ theme }) => ({
  maxWidth: theme.breakpoints.isMinWidthLaptop ? '800px' : '100%',
  alignItems: 'center',
  width: '100%',
  gap: theme.spacing['56px'],
}))

const IllustrationAndDisplayContainer = styled.View<{ theme: AuroraThemeType }>(({ theme }) => ({
  alignItems: 'center',
  gap: theme.spacing['24px'],
}))

const TitleAndPillsContainer = styled.View<{ theme: AuroraThemeType }>(({ theme }) => ({
  alignItems: 'center',
  gap: theme.spacing['32px'],
}))

const PillsContainer = styled.View<{ theme: AuroraThemeType }>(({ theme }) => ({
  flexDirection: theme.breakpoints.isMobileSized ? 'column' : 'row',
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing['12px'],
}))

const ButtonContainer = styled.View({
  alignItems: 'center',
})

const ExploreContainer = styled.View<{ theme: AuroraThemeType }>(({ theme }) => ({
  alignItems: 'center',
  gap: theme.spacing['16px'],
}))

const DescriptionTextContainer = styled.View({ marginTop: -8 })

const PillWrapper = styled.View({})

const PILL_OPTIONS = [
  { id: 'therapy', labelKey: 'therapy' },
  { id: 'wellness', labelKey: 'wellness' },
  { id: 'selfCare', labelKey: 'selfCare' },
]

export const AuroraHeroSection: FunctionComponent<AuroraHeroSectionProps> = ({
  pathwaysType = PATHWAYS_TYPE.SINGLE,
  onCreateAccountButtonPress,
  onProviderLedCardPress,
  onWellnessCheckinCardPress,
  onSelfCareCardPress,
  onNotifyMeOnLaunchPress,
  onExploreOnYourOwnCTAPress,
  selectedPillId,
  startDate,
  isRegistrationAndLoginDisabled,
  isAutoEnrollmentPathways = false,
  onSkipPress,
}) => {
  const pillPressHandlers: Record<string, (() => void) | undefined> = {
    therapy: onProviderLedCardPress,
    wellness: onWellnessCheckinCardPress,
    selfCare: onSelfCareCardPress,
  }

  const handlePillPress = (pillId: string) => {
    pillPressHandlers[pillId]?.()
  }
  const theme = useTheme() as AuroraThemeType
  const { breakpoints } = theme
  const isComingSoon = pathwaysType === PATHWAYS_TYPE.COMING_SOON
  const { formatDate, formatMessage } = useIntl()

  const headlineText = isComingSoon ? (
    <FormattedMessage defaultMessage='Get ready to feel your best' description='Coming soon hero section headline' />
  ) : (
    <FormattedMessage defaultMessage='Let’s help you feel your best' description='Hero section headline' />
  )

  const subheadText = isComingSoon ? (
    <FormattedMessage
      defaultMessage="We're putting the finishing touches on your new mental health benefits experience. It's designed to make finding the right care easy, flexible, and personalized to you."
      description='Coming soon subhead description'
    />
  ) : (
    <FormattedMessage
      defaultMessage='Choose one to get started'
      description='Title prompting user to select an option'
    />
  )

  const getPillLabel = (pillId: string) => {
    switch (pillId) {
      case 'therapy':
        return (
          <FormattedMessage
            defaultMessage='1-on-1 therapy and coaching'
            description='Therapy and coaching pill label'
          />
        )
      case 'wellness':
        return <FormattedMessage defaultMessage='Wellness check-in' description='Wellness check-in pill label' />
      case 'selfCare':
        return <FormattedMessage defaultMessage='Self care library' description='Self care library pill label' />
      default:
        return null
    }
  }

  const isSinglePathway = pathwaysType === PATHWAYS_TYPE.SINGLE

  // Hide the wellness check-in pill when it is unavailable (e.g. non-English languages),
  // leaving the two-pathway version of the header (therapy + self care).
  const pillOptions = onWellnessCheckinCardPress ? PILL_OPTIONS : PILL_OPTIONS.filter(({ id }) => id !== 'wellness')

  const renderDefaultContent = () => (
    <>
      {!isSinglePathway && (
        <TitleAndPillsContainer>
          <Title
            size={breakpoints.isMobileSized ? TitleSize.SMALL : TitleSize.MEDIUM}
            text={subheadText}
            textAlign='center'
            color={theme.colors.textInverse}
          />
          <PillsContainer>
            {pillOptions.map(({ id }) => (
              <PillWrapper key={id}>
                <WordCloudButton
                  text={getPillLabel(id)}
                  onPress={() => handlePillPress(id)}
                  isSelected={selectedPillId === id}
                  testID={tID(`AuroraHeroSection-pill-${id}`)}
                />
              </PillWrapper>
            ))}
          </PillsContainer>
        </TitleAndPillsContainer>
      )}
      <ButtonContainer>
        <PrimaryButton
          text={
            isAutoEnrollmentPathways ? (
              <FormattedMessage
                defaultMessage='Understand your coverage'
                description='CTA button text for auto enrollment users to understand their coverage'
              />
            ) : isSinglePathway ? (
              <FormattedMessage defaultMessage='Get started' description='Primary CTA button text for single pathway' />
            ) : (
              <FormattedMessage defaultMessage='Sign up and explore' description='Primary CTA button text' />
            )
          }
          onPress={() => onCreateAccountButtonPress?.()}
          testID={tID('HeroSection-registrationButton')}
        />
      </ButtonContainer>
      {isSinglePathway && (
        <PressableOpacity
          onPress={() => onExploreOnYourOwnCTAPress?.()}
          accessibilityRole='button'
          testID={tID('HeroSection-exploreOnYourOwnCTA')}
        >
          <ExploreContainer>
            <BodyText
              size={BodyTextSize.LARGE}
              text={<FormattedMessage defaultMessage='Explore on your own' description='Explore on your own CTA' />}
              textAlign='center'
              color={theme.colors.textInverse}
            />
            <ArrowIcon size={24} fillColor={theme.colors.textInverse} direction={ArrowIconDirection.DOWN} />
          </ExploreContainer>
        </PressableOpacity>
      )}
    </>
  )

  const renderComingSoonContent = () => (
    <>
      {!isRegistrationAndLoginDisabled && (
        <>
          <BodyText
            size={BodyTextSize.DEFAULT}
            text={subheadText}
            textAlign='center'
            color={theme.colors.textInverse}
          />
          <ButtonContainer>
            <PrimaryButton
              text={<FormattedMessage defaultMessage='Notify me on launch' description='Notify me CTA button text' />}
              onPress={() => onNotifyMeOnLaunchPress?.()}
              testID={tID('HeroSection-notifyMeOnLaunchButton')}
            />
          </ButtonContainer>
        </>
      )}
      <PressableOpacity
        onPress={() => onExploreOnYourOwnCTAPress?.()}
        accessibilityRole='button'
        testID={tID('HeroSection-exploreOnYourOwnCTA')}
      >
        <ExploreContainer>
          <BodyText
            size={BodyTextSize.LARGE}
            text={<FormattedMessage defaultMessage='Explore on your own' description='Explore on your own CTA' />}
            textAlign='center'
            color={theme.colors.textInverse}
          />
          <ArrowIcon size={24} fillColor={theme.colors.textInverse} direction={ArrowIconDirection.DOWN} />
        </ExploreContainer>
      </PressableOpacity>
    </>
  )

  return (
    <HeroContainer
      colors={theme.colors.components.heroSection.background.gradient}
      start={{ x: 0.5, y: 0 }}
      end={{ x: 0.5, y: 1 }}
      testID={tID('HeroSection-pathwaysHomepage')}
    >
      {isAutoEnrollmentPathways && onSkipPress && (
        <NavHeaderContainer>
          <TextButton
            onPress={onSkipPress}
            text={formatMessage({ defaultMessage: 'Skip', description: 'Skip button text to skip to next page' })}
            customTextColor={theme.colors.textInverse}
            testID={tID('Header-skip-button')}
          />
        </NavHeaderContainer>
      )}
      <ContentContainer>
        <IllustrationAndDisplayContainer>
          <OrbLottie size={breakpoints.isMobileSized ? 200 : 284} loop={false} />
          {isComingSoon && startDate && !isRegistrationAndLoginDisabled && (
            <Title
              size={TitleSize.MEDIUM}
              text={
                <FormattedMessage
                  defaultMessage='Launching {startDate}'
                  description='Title text indicating when date customer launches'
                  values={{
                    startDate: formatDate(startDate, {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                      timeZone: 'UTC',
                    }),
                  }}
                />
              }
              textAlign='center'
              color={theme.colors.textInverse}
            />
          )}
          <Display
            size={breakpoints.isMobileSized ? DisplaySize.SMALL : DisplaySize.MEDIUM}
            text={headlineText}
            textAlign='center'
            color={theme.colors.textInverse}
          />
          {isSinglePathway && (
            <DescriptionTextContainer>
              <BodyText
                size={BodyTextSize.DEFAULT}
                text={
                  <FormattedMessage
                    defaultMessage='Meet with world-class mental health experts and discover our on demand wellness resources.'
                    description='Summary of Lyra offerings'
                  />
                }
                textAlign='center'
                color={theme.colors.textInverse}
              />
            </DescriptionTextContainer>
          )}
        </IllustrationAndDisplayContainer>
        {isComingSoon ? renderComingSoonContent() : renderDefaultContent()}
      </ContentContainer>
    </HeroContainer>
  )
}
